import { Direction } from '@api/hooks/types/getTransactions';
import { DateRangeFormPickerWrapper } from '@components/CalendarDateRange';
import { ControlledTextInput } from '@components/ControlledTextInput';
import { FilterSection } from '@components/FilterSection';
import { FiltersModal } from '@components/FiltersModal';
import { PillButton } from '@components/PillButton';
import { useClearFilter } from '@hooks/useClearFilter';
import { OPERATION_TYPE } from '@pages/batch-operations/utils';
import type { FC } from 'react';
import {
	type Control,
	Controller,
	type SubmitHandler,
	useFormContext,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { OperationStatus } from 'types/operations';

export interface OperationsFilterParams {
	direction?: Direction;
	status?: OperationStatus[];
	dateFrom?: string;
	dateTo?: string;
	paymentType?: OPERATION_TYPE;
	documentNumber?: string;
	senderIban?: string;
	beneficiaryIban?: string;
	beneficiaryName?: string;
}

const DIRECTION_OPTIONS: Array<{
	labelKey: string;
	value: Direction | undefined;
}> = [
	{ labelKey: 'newOperations.filters.all', value: undefined },
	{ labelKey: 'newOperations.filters.outgoing', value: Direction.OUTWARD },
	{ labelKey: 'newOperations.filters.incoming', value: Direction.INWARD },
];

const STATUS_OPTIONS: Array<{ labelKey: string; value: OperationStatus }> = [
	{
		labelKey: 'listOfOperations.paymentStatuses.unsigned',
		value: OperationStatus.CREATED,
	},
	{ labelKey: 'listOfOperations.paymentStatuses.signed', value: OperationStatus.SIGNED },
	{
		labelKey: 'listOfOperations.paymentStatuses.processed',
		value: OperationStatus.PROCESSED,
	},
	{ labelKey: 'listOfOperations.paymentStatuses.failed', value: OperationStatus.FAILED },
	{
		labelKey: 'listOfOperations.paymentStatuses.executed',
		value: OperationStatus.EXECUTED,
	},
];

const PAYMENT_TYPE_OPTIONS: Array<{
	labelKey: string;
	value: OPERATION_TYPE | undefined;
}> = [
	{ labelKey: 'newOperations.filters.all', value: undefined },
	{ labelKey: 'filters.internal', value: OPERATION_TYPE.SEPA },
	{ labelKey: 'filters.international', value: OPERATION_TYPE.INTERNATIONAL },
];

interface OperationsFiltersModalProps {
	onFiltersSubmit: SubmitHandler<OperationsFilterParams>;
	hasActiveFilters?: boolean;
	compact?: boolean;
}

export const OperationsFiltersModal: FC<OperationsFiltersModalProps> = ({
	onFiltersSubmit,
	hasActiveFilters = false,
	compact = false,
}) => {
	const { t } = useTranslation();
	const filterForm = useFormContext<OperationsFilterParams>();
	const { control, handleSubmit } = filterForm;
	const { handleClear, hasFilters: hasFormFilters } =
		useClearFilter<OperationsFilterParams>(filterForm, onFiltersSubmit);

	return (
		<FiltersModal
			hasFilters={hasFormFilters || hasActiveFilters}
			onClear={handleClear}
			onSubmit={handleSubmit(onFiltersSubmit)}
		>
			{!compact && (
				<Controller
					control={control}
					name="direction"
					render={({ field }) => (
						<FilterSection label={t('newOperations.filters.direction')}>
							{DIRECTION_OPTIONS.map((opt) => (
								<PillButton
									key={opt.value ?? 'all'}
									active={field.value === opt.value}
									onClick={() => field.onChange(opt.value)}
								>
									{t(opt.labelKey)}
								</PillButton>
							))}
						</FilterSection>
					)}
				/>
			)}

			{!compact && (
				<Controller
					control={control}
					name="status"
					render={({ field }) => (
						<FilterSection label={t('newOperations.filters.status')}>
							{STATUS_OPTIONS.map((opt) => (
								<PillButton
									key={opt.value}
									active={(field.value ?? []).includes(opt.value)}
									onClick={() => {
										const selected = field.value ?? [];
										field.onChange(
											selected.includes(opt.value)
												? selected.filter((v) => v !== opt.value)
												: [...selected, opt.value],
										);
									}}
								>
									{t(opt.labelKey)}
								</PillButton>
							))}
						</FilterSection>
					)}
				/>
			)}

			<DateRangeFormPickerWrapper control={control as Control} disabledFuture />

			<Controller
				control={control}
				name="paymentType"
				render={({ field }) => (
					<FilterSection label={t('newOperations.filters.paymentType')}>
						{PAYMENT_TYPE_OPTIONS.map((opt) => (
							<PillButton
								key={opt.value ?? 'all'}
								active={field.value === opt.value}
								onClick={() => field.onChange(opt.value)}
							>
								{t(opt.labelKey)}
							</PillButton>
						))}
					</FilterSection>
				)}
			/>

			<ControlledTextInput label={t('filters.docNo')} name="documentNumber" />
			<ControlledTextInput label={t('filters.senderIban')} name="senderIban" />
			<ControlledTextInput label={t('filters.beneficiaryIban')} name="beneficiaryIban" />
			<ControlledTextInput label={t('filters.beneficiaryName')} name="beneficiaryName" />
		</FiltersModal>
	);
};
